import React, { useState, useMemo } from 'react';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { T, M, A } from 'TOMA';
import s from './Crm.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { CustomLink } from 'components/CustomLink';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { iCrmCompany } from 'services/Crm/Crm.type';
import useModalContext from 'hooks/useModalContext';

const columnHelper = createColumnHelper<iCrmCompany>();

function CrmCompanies(): JSX.Element {
  const { setModalContent, setModalVisible } = useModalContext();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { crmRepository } = useApiContext();
  const [reload, setReload] = useState<number>(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    response = [],
    loading: dataLoading,
    error,
  } = useRepo(async () => {
    return await crmRepository.getCompanies();
  }, [reload]);

  const handleDelete = async (companyId: number): Promise<void> => {
    setModalContent(
      <div>
        <A.Text fontSize="22" fw="bold" mb="20">
          {t('crm:delete_company_confirm')}
        </A.Text>
        <div className={s.formActions}>
          <button
            className={s.btnDanger}
            onClick={async () => {
              try {
                await crmRepository.deleteCompany(companyId);
                toast.success(t('crm:company_deleted'));
                setModalVisible(false);
                setReload((e) => e + 1);
              } catch (err: any) {
                toast.error(err.message);
              }
            }}
          >
            {t('crm:confirm')}
          </button>
          <button className={s.btnLink} onClick={() => setModalVisible(false)}>
            {t('crm:cancel')}
          </button>
        </div>
      </div>
    );
    setModalVisible(true);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('crm:company_name'),
        cell: (info) => (
          <CustomLink
            to={routes.crmCompanyDetail.to({
              companyId: info.row.original.id,
            })}
            color="info"
          >
            <strong>{info.getValue()}</strong>
          </CustomLink>
        ),
      }),
      columnHelper.accessor('city', {
        header: t('crm:city'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('country', {
        header: t('crm:country'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('phone', {
        header: t('crm:phone'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('email', {
        header: t('crm:email'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.display({
        id: 'actions',
        header: t('crm:actions'),
        cell: ({ row }) => (
          <div className={s.tableActions}>
            <CustomLink
              to={routes.crmCompanyEdit.to({
                companyId: row.original.id,
              })}
              color="info"
            >
              <span className={s.actionBtn}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6b7280"
                  strokeWidth="2"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </span>
            </CustomLink>
            <button
              type="button"
              className={s.actionBtnDanger}
              onClick={async () => await handleDelete(row.original.id)}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        ),
      }),
    ],
    [t]
  );

  const data = useMemo(
    () =>
      response?.filter((company: iCrmCompany) => {
        if (!globalFilter) return true;
        const search = globalFilter.toLowerCase();
        return (
          (company.name?.toLowerCase().includes(search) ?? false) ||
          (company.city?.toLowerCase().includes(search) ?? false) ||
          (company.email?.toLowerCase().includes(search) ?? false)
        );
      }) ?? [],
    [response, globalFilter]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div>
      <T.Container>
        <M.Loading loading={dataLoading} error={error} fullWiewport>
          <div className={s.pageHeader}>
            <h1 className={s.pageTitle}>{t('crm:companies_management')}</h1>
            <button
              className={s.btnPrimary}
              onClick={() => navigate(routes.crmCompanyNew.path)}
            >
              {t('crm:new_company')}
            </button>
          </div>

          <div className={s.toolbar}>
            <div />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={t('crm:search_placeholder')}
              className={s.searchInput}
            />
          </div>

          {data.length > 0 ? (
            <div className={s.tableWrap}>
              <table className={s.Table}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          style={{ cursor: 'pointer' }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' \u2191',
                            desc: ' \u2193',
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={s.row}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          <div className={s.row__div}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !dataLoading && (
              <div className={s.emptyState}>
                <div className={s.emptyIcon}>&#127970;</div>
                <p className={s.emptyTitle}>{t('crm:no_results')}</p>
              </div>
            )
          )}
        </M.Loading>
      </T.Container>
    </div>
  );
}

export default CrmCompanies;
